import React, { useMemo, useState } from 'react';

export type ChartBand = 'evening' | 'morning' | 'earlymorning';
export interface ChartResult { first: Date; final: Date; band: ChartBand; }

/** Chart preparation times per the article's published rule table.
 *  >=14:00  -> first chart = departure - 8h
 *  05:00-13:59 -> first chart = 21:00 the previous day
 *  00:00-04:59 -> first chart = 20:00 the previous day
 *  final chart is always departure - 30 minutes. */
export function chartTimes(departure: Date): ChartResult {
  const h = departure.getHours();
  const final = new Date(departure.getTime() - 30 * 60000);
  const prevDayAt = (hh: number) => {
    const d = new Date(departure);
    d.setDate(d.getDate() - 1);
    d.setHours(hh, 0, 0, 0);
    return d;
  };
  if (h >= 14) return { first: new Date(departure.getTime() - 8 * 3600000), final, band: 'evening' };
  if (h >= 5) return { first: prevDayAt(21), final, band: 'morning' };
  return { first: prevDayAt(20), final, band: 'earlymorning' };
}

const fmtTime = (d: Date) =>
  d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
const fmtDay = (d: Date, ref: Date) => {
  const same = d.toDateString() === ref.toDateString();
  return same ? 'the same day' : d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' });
};

const ChartPrepCalculator: React.FC = () => {
  const [time, setTime] = useState('16:30');
  const [dateStr, setDateStr] = useState(() => new Date().toISOString().slice(0, 10));

  const dep = useMemo(() => {
    const [hh, mm] = time.split(':').map(Number);
    const [y, mo, d] = dateStr.split('-').map(Number);
    return new Date(y, mo - 1, d, hh, mm, 0, 0);
  }, [time, dateStr]);

  const result = useMemo<ChartResult | null>(() => {
    const [hh, mm] = time.split(':').map(Number);
    const [y, mo, d] = dateStr.split('-').map(Number);
    if ([hh, mm, y, mo, d].some(Number.isNaN)) return null;
    return chartTimes(new Date(y, mo - 1, d, hh, mm, 0, 0));
  }, [time, dateStr]);

  return (
    <div className="not-prose my-8 rounded-2xl border border-blue-200 bg-blue-50 p-5 sm:p-6">
      <h3 className="mt-0 mb-4 text-lg font-bold text-gray-900">Chart Preparation Time Calculator</h3>
      <div className="flex flex-wrap gap-4 items-end">
        <label className="flex flex-col text-sm font-medium text-gray-700">
          Train departure date
          <input type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)}
                 className="mt-1 rounded-lg border border-gray-300 px-3 py-2 text-base" />
        </label>
        <label className="flex flex-col text-sm font-medium text-gray-700">
          Departure time
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
                 className="mt-1 rounded-lg border border-gray-300 px-3 py-2 text-base" />
        </label>
      </div>
      {result && (
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl bg-white p-4 border border-gray-200">
            <div className="text-xs font-semibold uppercase tracking-wide text-blue-600">First chart prepared</div>
            <div className="text-2xl font-bold text-gray-900">{fmtTime(result.first)}</div>
            <div className="text-sm text-gray-500">{fmtDay(result.first, dep)}</div>
          </div>
          <div className="rounded-xl bg-white p-4 border border-gray-200">
            <div className="text-xs font-semibold uppercase tracking-wide text-blue-600">Final chart prepared</div>
            <div className="text-2xl font-bold text-gray-900">{fmtTime(result.final)}</div>
            <div className="text-sm text-gray-500">30 minutes before departure</div>
          </div>
        </div>
      )}
      {result && (
        <p className="mt-4 mb-0 text-[17px] text-gray-700">
          Your <strong>{fmtTime(dep)}</strong> train&apos;s first chart is prepared around{' '}
          <strong>{fmtTime(result.first)}</strong> ({fmtDay(result.first, dep)}); the final chart at{' '}
          <strong>{fmtTime(result.final)}</strong>, 30 minutes before departure.
        </p>
      )}
    </div>
  );
};

export default ChartPrepCalculator;
